import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { api } from '../../context/AuthContext';

const errorText = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

/** Opt-in auto-top-up: buy a pack when credits drop below a threshold, up to a monthly cap. */
const AutoTopUpCard = ({ packs }) => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/billing/auto-topup').then((res) => setSettings(res.data)).catch(() => {});
  }, []);

  if (!settings) return null;

  const update = (patch) => setSettings((prev) => ({ ...prev, ...patch }));
  const save = async (next = settings) => {
    setSaving(true);
    try {
      const res = await api.put('/billing/auto-topup', {
        enabled: next.enabled,
        threshold: Number(next.threshold),
        pack_id: next.pack_id,
        monthly_cap: Number(next.monthly_cap),
      });
      setSettings(res.data);
      toast.success(res.data.enabled ? 'Auto-top-up is on' : 'Auto-top-up is off');
    } catch (error) {
      toast.error(errorText(error, 'Could not save auto-top-up settings'));
    } finally {
      setSaving(false);
    }
  };

  const pack = packs?.[settings.pack_id];
  const disabled = !settings.can_edit || saving;

  return (
    <Card className="border-slate-100" data-testid="auto-topup-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><RefreshCw className="h-5 w-5 text-emerald-500" />Auto-top-up</CardTitle>
        <CardDescription>
          Never run out mid-campaign: buy a credit pack automatically when your balance runs low. Off by default,
          and never more than your monthly limit.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!settings.can_edit && (
          <p className="text-sm text-slate-500">Only the account owner can change auto-top-up.</p>
        )}
        <div className="flex items-center justify-between">
          <Label htmlFor="auto-topup-enabled">Buy credits automatically</Label>
          <Switch
            id="auto-topup-enabled"
            checked={settings.enabled}
            disabled={disabled || (!settings.enabled && !settings.has_payment_method)}
            onCheckedChange={(enabled) => { const next = { ...settings, enabled }; setSettings(next); save(next); }}
            data-testid="auto-topup-switch"
          />
        </div>
        {!settings.has_payment_method && (
          <p className="text-xs text-amber-600">Buy a credit pack once to save a card, then you can turn this on.</p>
        )}
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label htmlFor="auto-topup-threshold">When credits fall below</Label>
            <Input id="auto-topup-threshold" type="number" min={1} value={settings.threshold} disabled={disabled}
              onChange={(e) => update({ threshold: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="auto-topup-pack">Buy</Label>
            <select
              id="auto-topup-pack"
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
              value={settings.pack_id}
              disabled={disabled}
              onChange={(e) => update({ pack_id: e.target.value })}
            >
              {Object.entries(packs || {}).map(([id, p]) => (
                <option key={id} value={id}>{p.credits.toLocaleString()} credits (${p.price})</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="auto-topup-cap">Monthly limit ($)</Label>
            <Input id="auto-topup-cap" type="number" min={pack?.price || 1} max={settings.max_monthly_cap}
              value={settings.monthly_cap} disabled={disabled}
              onChange={(e) => update({ monthly_cap: e.target.value })} />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500">
            Spent this month: ${settings.spent_this_month} of ${settings.monthly_cap}
          </p>
          <Button size="sm" variant="outline" onClick={() => save()} disabled={disabled}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AutoTopUpCard;
